<template>
  <div class="main-collection critical-component-clear">
    <div
      id="categorypage-nosto-1"
      class="nosto_element"
    />

    <boost-facet-carousel
      v-if="liquid.showBannerCarousel && facetCarousel"
      block-class="main-collection"
      :facet-carousel="facetCarousel"
      @carousel-facet="handleCarouselFacet"
    />

    <breadcrumbs
      v-if="liquid.collectionId"
      :title="liquid.collectionTitle"
    />

    <div
      v-if="!liquid.noResults"
      class="main-collection__header"
    >
      <h1
        :id="`section-title-${liquid.sectionId}`"
        class="main-collection__title"
        v-text="pageTitle"
      />

      <div
        v-if="description"
        class="main-collection__description"
        v-html="description"
      />
    </div>

    <search-bar
      v-if="liquid.noResults"
      class="main-collection__search-bar"
    />

    <div
      class="main-collection__mobile-controls"
    >
      <btn
        v-if="!noProducts"
        class="boost-filter__button-mobile"
        :label="$string('collection.filter')"
        modifiers="outlined"
        icon="filters"
        show-icon
        @click="handleOpenMobileDrawer"
      />

      <btn
        v-if="!noProducts"
        class="main-collection__grid-button"
        label="1X"
        :class="{ 'active': mobileGridCols === 1 }"
        modifiers="outlined"
        :show-icon="false"
        @click="updateMobileGridCols(1)"
      />
      <btn
        v-if="!noProducts"
        class="main-collection__grid-button"
        :class="{ 'active': mobileGridCols === 2 }"
        label="2X"
        modifiers="outlined"
        :show-icon="false"
        @click="updateMobileGridCols(2)"
      />
    </div>

    <boost-filter
      v-show="!isMobile && !liquid.noResults"
      :choices="choices"
      :filter="filter"
      :sort-by-options="liquid.sortByOptions"
      :total-product="totalProduct"
      :title="liquid.collectionTitle"
      @choices-updated="choicesUpdated"
      @facets-ready="updateChoicesOnLoad"
      @clear-choices="clearChoices"
    />

    <div
      v-if="noProducts"
      class="boost-filter__error-message"
      v-html="noResultsString"
    />

    <div
      v-if="boostReady"
      class="main-collection__grid grid"
      :class="{ 'single-col-mobile': mobileGridCols === 1 }"
    >
      <div
        v-for="(product, index) of products"
        :key="`product-card-${liquid.sectionId}-${product.id}-${index}`"
        :class="{
          'in-grid-banner__block': product.promoShow,
          'in-grid-banner__single': product.promoShow && !product.promoDoubleWidth,
          'in-grid-banner__double': product.promoDoubleWidth,
          'main-collection__hero-banner': product.showHero,
          'image-and-text-accordion__wrap': product.imageTextActive
        }"
      >
        <product-card
          v-if="product.id"
          class="main-collection__product-card"
          :index="index"
          :product="product"
          :collection="liquid.collectionHandle"
          @click="handleAnalyticsEvent(product.id.toString(), $event)"
        />

        <in-grid-banner
          v-if="product.promoShow"
          :collection-meta="product"
          @section-mounted="() => reOrderForSpace(product?.promoIndex)"
        />
      </div>
    </div>

    <div
      v-else
      class="main-collection__grid grid"
      :class="{ 'single-col-mobile': mobileGridCols === 1 }"
    >
      <div
        v-for="(product, index) of products"
        :key="`product-card-${liquid.sectionId}-${product.id}-${index}`"
        :class="{
          'in-grid-banner__block': product.promoShow,
          'in-grid-banner__single': product.promoShow && !product.promoDoubleWidth,
          'in-grid-banner__double': product.promoDoubleWidth,
          'main-collection__hero-banner': product.showHero,
          'image-and-text-accordion__wrap': product.imageTextActive
        }"
      >
        <product-card
          v-if="product.id"
          :index="index"
          :product="product"
          wishlist
          block-class="main-collection"
          @click="handleAnalyticsEvent(product.id.toString(), $event)"
        />
      </div>
    </div>

    <boost-pagination
      v-if="!liquid.noResults"
      :limit="limit"
      :page="page"
      :total-product="totalProduct"
      @choices-updated="choicesUpdated"
    />
    <boost-analytics
      :analytics-event="analyticsEvent"
      type="filter"
    />
    <boost-api-call
      build-filter-tree
      :query="query"
      :choices="choices"
      :collection-id="liquid.collectionId"
      :endpoint="endpoint"
      :limit="limit"
      :shop="$variable('store.permanentDomain').replace(/^\/\//g, '').replace(/\/$/g, '')"
      event-type="init"
      @boost-updated="boostUpdated"
    />
    <div
      id="categorypage-nosto-2"
      class="nosto_element"
    />

    <div
      v-if="liquid.internalLinksArray?.length > 0"
      class="main-collection__explore-more"
    >
      <div
        v-for="(linkObject,index) of liquid.internalLinksArray"
        :key="index"
        class="main-collection__explore-more-content"
      >
        <h2
          class="main-collection__explore-more-title"
        >
          {{ linkObject.title }}
        </h2>
        <ul class="main-collection__explore-more-linklist">
          <li
            v-for="(link, index) of linkObject.links"
            :key="index"
          >
            <a :href="link.url" class="main-collection__explore-more-link">{{ link.label }}</a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Main collection component.
 *
 * @param {Number} liquid.collectionId - Collection ID.
 * @param {String} liquid.collectionDescription - Collection description.
 * @param {String} liquid.collectionTitle - Collection title.
 * @param {Boolean} liquid.noResults - If this is a search page with no results.
 * @param {Number} liquid.productsPerPage - Products per page.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Object} liquid.sortByOptions - Object of sort by options.
 * @param {Object} liquid.inGridBanners - Array with in-grid banners settings.
 * @param {Array} liquid.internalLinksArray - Metafields Array.
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import classes from '~/config/classes'

import Breadcrumbs from '~async/breadcrumbs/breadcrumbs.vue'
import InGridBanner from '~async/in-grid-banner/in-grid-banner'
import BoostFacetCarousel from '~async/main-collection/boost-facet-carousel'
import BoostFilter from '~async/main-collection/boost-filter'
import BoostPagination from '~async/main-collection/boost-pagination'
import ProductCard from '~async/product-card/product-card'
import SearchBar from '~async/search-bar/search-bar'
import BoostAnalytics from '~global/boost-analytics/boost-analytics'
import BoostApiCall from '~global/boost-api-call/boost-api-call'
import Btn from '~global/btn/btn'

import { formatBoostProduct } from '~/helpers/format'
import { handleize } from '~/helpers/general'

export default {
  name: 'MainCollection',

  components: {
    Btn,
    BoostAnalytics,
    BoostApiCall,
    BoostFacetCarousel,
    BoostFilter,
    BoostPagination,
    Breadcrumbs,
    ProductCard,
    InGridBanner,
    SearchBar,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        collectionId: '',
        collectionDescription: '',
        collectionTitle: '',
        collectionHandle: '',
        facetCarousel: [],
        noResults: false,
        productsPerPage: '',
        sectionId: '',
        sortByOptions: {},
        filteredTag: '',
        internalLinksArray: [],
        inGridBanners: [
          {
            promoPosition: 0,
            promoShow: null,
            promoDoubleWidth: null,
            promoDesktopImage: null,
            promoMobileImage: null,
            promoBackgroundColor: null,
            promoVideoOnly: null,
            promoVideo: null,
            promoHeader: null,
            promoHeaderColor: null,
            promoCtaColor: null,
            promoCtaLabel: null,
            promoLink: null,
            promoIndex: 0,
          },
        ],
      }),
    },
  },

  data() {
    return {
      analyticsEvent: {},
      boostReady: false,
      boostInitialized: false,
      choices: null,
      filter: [],
      mobileGridCols: 2,
      noProducts: false,
      page: 1,
      products: [],
      query: null,
      totalProduct: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute number of extra, non-product 'cards'
     * added to grid by in-grid section blocks.
     * @returns {Number}
     */
    extraGridCards() {
      let cards = 0

      if (!this.showBlocks) {
        return cards
      }

      this.liquid.inGridBanners.forEach((inGridBanner) => {
        if (inGridBanner.promoShow) {
          if (this.isMobile) {
            cards += this.mobileGridCols
          } else if (!inGridBanner.promoDoubleWidth) {
            cards += 1
          } else if (inGridBanner.promoDoubleWidth) {
            cards += 2
          }
        }
      })

      return cards
    },

    /**
     * Compute number of product card gaps at end of page.
     * @returns Number
     */
    gridEndGap() {
      const columns = this.isMobile ? this.mobileGridCols : 3
      if (!this.showBlocks) {
        return 0
      }
      const totalGridCards = this.liquid.productsPerPage + this.extraGridCards
      const remainder = totalGridCards % columns
      return remainder === 0
        ? remainder
        : columns - remainder
    },

    /**
     * Compute number of products to get from Boost
     * @returns Number
     */
    limit() {
      if (this.showBlocks) {
        return this.liquid.productsPerPage + this.gridEndGap
      }
      return this.liquid.productsPerPage
    },

    /**
     * Compute drawer choices.
     * @returns {String}
     */
    drawerChoices() {
      return this.getChoices()
    },

    /**
     * Returns boolean based on choices and URL parameters
     * @returns {Boolean}
     */
    showBlocks() {
      if (
        this.choices === '' ||
        this.choices === '&page=1' ||
        window.location.search.toLowerCase().includes('email') ||
        Shopify.designMode
      ) {
        return true
      }

      return false
    },

    /**
     * Compute page title based on page type.
     */
    pageTitle() {
      let title

      switch (window.cnvs.page.type) {
        case 'collection':
          title = this.collectionPageTitle
          break
        case 'search':
          title = this.$string('search.title', {
            replace: {
              num: this.totalProduct,
              query: this.query,
            },
          })
          break
      }

      return title
    },

    /**
     * Compute page title using special rules for collections.
     * @returns {String}
     */
    collectionPageTitle() {
      if (!this.choices) {
        return this.liquid.collectionTitle
      }

      const firstChoice = this.getFirstChoiceValue()

      if (!firstChoice) {
        return this.liquid.collectionTitle
      }

      const choiceHandle = handleize(firstChoice)

      return this.filtersIncludeKey(choiceHandle)
        ? window.filters[`${choiceHandle}`].displayName
        : this.liquid.collectionTitle
    },

    /**
     * Set to use the 'search/suggest' endpoint for the API.
     */
    endpoint() {
      let endpoint

      switch (window.cnvs.page.type) {
        case 'collection':
          endpoint = 'filter'
          break
        case 'search':
          endpoint = 'search'
          break
      }

      return endpoint
    },

    /**
     * Returns carousel tile facets.
     */
    facetCarousel() {
      if (!this.liquid.showBannerCarousel) {
        return false
      }

      const facets = JSON.parse(this.liquid.facetCarousel)

      if (!facets || facets.length < 1) {
        return false
      }

      return facets
    },

    /**
     * Compute collection description.
     * @returns {String}
     */
    description() {
      if (!this.choices) {
        return this.liquid.collectionDescription
      }

      const firstChoice = this.getFirstChoiceValue()

      if (!firstChoice) {
        return this.liquid.collectionDescription
      }

      const choiceHandle = handleize(firstChoice)

      return this.filtersIncludeKey(choiceHandle)
        ? window.filters[`${choiceHandle}`].description
        : this.liquid.collectionDescription
    },

    /**
     * Compute extra elements for breadcrumbs.
     * @returns {Array}
     */
    breadcrumbExtras() {
      if (!this.choices) {
        return []
      }

      const firstChoice = this.getFirstChoiceValue()

      if (!firstChoice) {
        return []
      }

      const choiceHandle = handleize(firstChoice)

      let title = firstChoice

      if (this.filtersIncludeKey(choiceHandle)) {
        title = window.filters[`${choiceHandle}`].displayName
      }

      if (title.includes(':')) {
        title = title.split(':')[1]
      }

      return [
        {
          title,
        },
      ]
    },

    /**
     * Compute no results string.
     * @returns {String}
     */
    noResultsString() {
      if (this.choices) {
        return this.$string('collection.no_filter_results')
      }

      return this.$string('search.no_results').replace(
        '"{{ query }}"',
        `<b>"${this.query}"</b>`,
      )
    },
  },

  watch: {

    /**
     * Watch drawer choices to update URL.
     */
    drawerChoices(choices) {
      if (choices === this.choices) {
        return
      }

      this.choices = choices
      this.boostReady = false
      this.updateWindowLocationSearch(this.choices)
    },

    boostReady() {
      if (!this.boostInitialized) {
        this.boostInitialized = true
        this.setComponentState({
          handle: 'collection-ready',
          state: 'active',
        })
      }
    }
  },

  mounted() {
    this.getSearchParams()
    this.setEventListeners()

    if (window.cnvs.page.type === 'search') {
      this.setSearchQuery()
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      updateChoices: 'main-collection/updateChoices',
      updateFilters: 'main-collection/updateFilters',
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getChoices: 'main-collection/getChoices',
      getFilters: 'main-collection/getFilters',
    }),

    /**
     * Open mobile drawer on filter button click.
     */
    handleOpenMobileDrawer() {
      this.openOverlay({
        component: 'boost-drawer-mobile',
        ignoreDismissed: true,
        namespace: 'boostDrawerMobile',
      })
    },

    /**
     * Check window.location.search on load to see if filter choices exist
     * before making API call.
     */
    getSearchParams() {
      if (!window.location.search.includes('?')) {
        this.choices = ''

        if (this.liquid.filteredTag !== '') {
          this.choices = `&pf_opt_color%5B%5D=${this.liquid.filteredTag[0].toUpperCase() + this.liquid.filteredTag.slice(1)}`
        }

        this.boostReady = false
        this.updateChoices(this.choices)

        return
      }

      const urlString = window.location.search.split('?')[1]

      const choicesArray = urlString.split('&').filter((choice) => {
        const filterOptionId = choice.split('=')[0]

        if (
          filterOptionId.includes('pf_') ||
          filterOptionId.includes('page') ||
          (filterOptionId.includes('sort') && choice !== '')
        ) {
          return choice
        }

        return false
      })

      const formatted = choicesArray.map((choice) => `&${choice}`)
      this.choices = formatted.join('')
      this.boostReady = false
      this.updateChoices(this.choices)
    },

    /**
     * Update number of columns that display on mobile and button classes.
     * @param {Number} columns
     */
    updateMobileGridCols(columns) {
      this.mobileGridCols = columns
    },

    /**
     * Update window location search and browser history after choices updated.
     * @param {String} choices - Updated user choices.
     */
    updateWindowLocationSearch(choices) {
      let url = window.location.href.replace(window.location.search, `?${choices}`)

      if (window.location.search === '') {
        url = window.location.href.replace(window.location.href, `${window.location.href}?${choices}`)
      }

      if (!url.includes('&')) {
        url = url.replace('?', '')
      }

      window.history.pushState({}, '', url)
    },

    /**
     * Update pagination.
     * @param {String} pageNumber - Page number from choices.
     */
    updatePagination(pageNumber) {
      if (!pageNumber) {
        if (this.totalProduct > this.products.length) {
          this.page = 1
        }

        return
      }

      this.page = parseInt(pageNumber)
    },

    /**
     * Update facets on load.
     */
    updateChoicesOnLoad() {
      const facetValues = [...document.querySelectorAll('[js-boost-facet="value"]')]

      const choicesArray = this.choices.split('&').filter((choice) => {
        return choice !== ''
      })

      let pageNumber

      facetValues.forEach((element) => element.classList.remove(this.$class('active')))

      choicesArray.forEach((choice) => {
        const value = choice.split('=')[1]
        const decoded = decodeURI(value)

        const activeValue = facetValues.find((element) => {
          return element.id === decoded
        })

        if (choice.includes('page')) {
          pageNumber = value
        }

        if (!activeValue) {
          return
        }

        activeValue.classList.add(this.$class('active'))
      })

      this.updatePagination(pageNumber)
    },

    /**
     * Clear choices when 'clear all' button is clicked.
     */
    clearChoices() {
      this.choices = ''
      this.boostReady = false
      this.updateChoices(this.choices)
      this.updateWindowLocationSearch(this.choices)
    },

    /**
     * Callback after user choices emitted from facets to filter.
     * @param {Object} response - Response object with choices update.
     */
    choicesUpdated(response) {
      const { requestParam, action } = response

      const pageChoice = this.choices.split('&').find((choice) => {
        return choice.includes('page')
      })

      const sortChoice = this.choices.split('&').find((choice) => {
        return choice.includes('sort')
      })

      if (pageChoice) {
        this.choices = this.choices.split(`&${pageChoice}`).join('')
      }

      if (requestParam.includes('sort') && sortChoice) {
        this.choices = this.choices.split(`&${sortChoice}`).join('')
      }

      if (action === 'add') {
        this.choices += requestParam
      } else {
        this.choices = this.choices.split(requestParam).join('')
      }

      this.boostReady = false
      this.updateChoices(this.choices)
      this.updateWindowLocationSearch(this.choices)
    },

    /**
     * Logic for ordering modules via theme editor.
     */
    modulesOrdering() {
      const inGridBanners = this.liquid.inGridBanners

      inGridBanners.forEach((inGridBanner) => {
        const promoPosition = inGridBanner.promoPosition > 0 ? inGridBanner.promoPosition : 0

        if (inGridBanner.promoShow) {
          this.products.splice(promoPosition, 0, inGridBanner)
        }
      })
    },

    /**
     * Callback after a successfull API call.
     * @param {Object} data - Response object from the API call.
     */
    boostUpdated(data) {
      this.products = data.products.map((product) => {
        return formatBoostProduct(product)
      })

      if (this.showBlocks) {
        this.modulesOrdering()
      }

      this.filter = data.filter.options
      this.totalProduct = data.total_product
      this.noProducts = !this.products.length
      this.setFilterSwatches()
      this.updateFilters(this.filter)
      this.boostReady = true
    },

    /**
     * Add next sibling of image & text accordion if there is a space before it.
     * Prevents the empty space from being visible.
     * @param {Number} index - In Grid Banner promo index.
     */
    reOrderForSpace(index = 0) {
      const inGridBannerBlock = document.querySelector(
        '.in-grid-banner__double',
      )

      const promoPositionSetting = this.liquid.inGridBanners[index].promoPosition
      const promoPosition = promoPositionSetting > 0 ? promoPositionSetting : 0

      if (
        this.isMobile &&
        promoPosition % this.mobileGridCols !== 0
      ) {
        this.alterProductsPosition(inGridBannerBlock)
      }

      /*
       * % 3 === 2 means, in grid banner is last element in the row.
       * And double width causes issue on desktop.
       */
      if (
        !this.isMobile &&
        inGridBannerBlock &&
        promoPosition % 3 === 2
      ) {
        this.alterProductsPosition(inGridBannerBlock, true)
      }
    },

    /**
     * Moves next element sibling which is product to previous placement
     * to overcome spacing issues before in-grid blocks
     * @param {Element} node - Parent node to move siblings.
     * @param {Boolean} [forceMove] - Force to prepend next sibling to before.
     */
    alterProductsPosition(node, forceMove = false) {
      if (!node) {
        return
      }

      const previousSibling = node.previousElementSibling

      if (!previousSibling) {
        return
      }

      if (forceMove) {
        const nextElement = node.nextElementSibling

        node.insertAdjacentElement('beforebegin', nextElement)
        return
      }

      let offset = 0

      /**
       * Means, the node is full width and need to calculate offset.
       */
      if (
        Math.floor(node.getBoundingClientRect().width) === window.innerWidth
      ) {
        const collectionElement = document.querySelector('.main-collection')

        if (!collectionElement) {
          return
        }

        offset = parseInt(
          window.getComputedStyle(collectionElement).paddingRight,
        )
      }

      /**
       * Means, there is a space before the image & text accordion
       */
      if (
        Math.floor(previousSibling.getBoundingClientRect().right) !==
        Math.floor(node.getBoundingClientRect().right - offset)
      ) {
        const nextElement = node.nextElementSibling

        if (!nextElement) {
          return
        }

        node.insertAdjacentElement('beforebegin', nextElement)
        this.alterProductsPosition(node)
      }
    },

    /**
     * Event handler to close any open filters on body click.
     * @param {Event} event - Click event.
     */
    handleBodyClick(event) {
      const facets = document.querySelectorAll('[js-boost-facet="facet"]')
      const sortBy = document.querySelector('[js-boost-filter="sortBy"]')
      let bodyClick = true

      facets.forEach((facet) => {
        if (!facet.contains(event.target)) {
          return
        }

        bodyClick = false
      })

      if (sortBy !== null && sortBy.contains(event.target)) {
        bodyClick = false
      }

      if (!bodyClick) {
        return
      }

      facets.forEach((facet) => {
        facet.classList.remove(this.$class('active'))
      })

      if (sortBy === null) {
        return
      }

      sortBy.classList.remove(this.$class('active'))
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      window.addEventListener('click', this.handleBodyClick)
      window.onpopstate = () => this.getSearchParams()
    },

    /**
     * Set swatches for use in Boost Filter.
     */
    setFilterSwatches() {
      const data = this.filter.find((option) => {
        return option.filterType === 'opt_color'
      })

      if (!data) {
        return
      }

      const swatches = data.values.map((swatch) => {
        const key = swatch.key.toLowerCase()

        const formatted = {
          key,
          swatch: `color-${key}_12x12.png`,
        }

        return formatted
      })

      window.cnvs = window.cnvs || {}
      window.cnvs.filterSwatches = swatches
    },

    /**
     * Pass data from analytics events to analytics component.
     * @param {String} productId - Product ID to string.
     * @param {Event} event - Click event.
     */
    handleAnalyticsEvent(productId, event) {
      if (event.target.classList.contains('product-card__swatch-image')) {
        return
      }

      this.analyticsEvent = {
        pid: productId,
        u: 'view_product',
      }
    },

    /**
     * Set search query from URL parameters.
     */
    setSearchQuery() {
      if (!window.location.search.includes('?')) {
        this.query = false
        return
      }

      const urlParams = window.location.search.split('?')[1].split('&')
      const queryParam = urlParams.find((param) => {
        return param.includes('q=')
      })

      if (!queryParam) {
        return
      }

      this.query = queryParam.split('=')[1]
    },

    /**
     * Trigger handleCarouselFacetUpdate when mobile facet values updates
     */
    mobileFacetValues() {
      if (!this.waitingMobileApplyReady) {
        return
      }

      this.handleCarouselFacetUpdate(this.mobileFacetValues)
    },

    /**
     * Handle carousel facet click and triggers filter facets by matching id.
     * @param {String} id - facet id from carousel tile.
     */
    handleCarouselFacet(id) {
      if (!id) {
        return
      }

      const facet = this.getFacetById([...document.querySelectorAll('[js-boost-facet="value"]')], id)

      if (!facet) {
        this.clearChoices()
        this.waitingCarouselFacetUpdate = id
        return
      }

      if (!facet.classList.contains(classes.active)) {
        this.clearChoices()
        facet.click()
      }

      this.waitingCarouselFacetUpdate = false
    },

    /**
     * Checks if any rendered facet matches the recently clicked carousel facet.
     * @param {Array} facetValues - facet elements
     */
    handleCarouselFacetUpdate(facetValues) {
      const facet = this.getFacetById(facetValues, this.waitingCarouselFacetUpdate)

      if (facet) {
        this.waitingCarouselFacetUpdate = false

        facet.click()
      }
    },

    /**
     * Get the facet by id.
     * @param {Array} facetValues - facet elements
     * @param {String} id - facet id to match element
     * @return {HTMLElement}
     */
    getFacetById(facetValues, id) {
      const regexId = new RegExp(`^${id}$`, 'i')
      const findFacet = facetValues.find((facet) => regexId.test(facet.id))
      return findFacet
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('boostDrawerMobile:applyFilterReady', (mobileApplyFilterBtn) => {
        if (!this.waitingMobileApplyReady) {
          return
        }

        this.waitingCarouselFacetUpdate = false
        this.waitingMobileApplyReady = false
        mobileApplyFilterBtn.click()
      })
    },

    /**
     * Get first filter choice
     * @returns {String} - handleized filter choice.
     */
    getFirstChoiceValue() {
      if (!this.choices) {
        return false
      }

      const choices = this.choices.split('=')

      if (!choices || choices.length <= 1) {
        return false
      }

      const firstChoice = choices[1].split('&')[0]

      return firstChoice
    },

    /**
     * Determine if filters includes key
     * @param {String} key - filter key
     * @returns {Boolean}
     */
    filtersIncludeKey(key) {
      return window.filters && Object.keys(window.filters).includes(key)
    },
  },
}
</script>

<style lang="scss">
@import './main-collection';
</style>
